import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import type {
  OpenerDescalceAccountKey,
  OpenerDescalceAccountStatus,
  OpenerDescalceSnapshot
} from '../../api/useOpenerDescalce';
import type { DescalceTotals } from './descalceFilters';

const ACCOUNT_LABEL: Record<OpenerDescalceAccountKey, string> = {
  prod: 'PROD',
  test: 'TEST'
};

const SOURCE_COLOR: Record<
  OpenerDescalceAccountStatus['source'],
  'success' | 'warning' | 'error' | 'default'
> = {
  venue: 'success',
  starting: 'warning',
  unconfigured: 'default',
  error: 'error'
};

const AccountChip = ({
  account,
  status
}: {
  account: OpenerDescalceAccountKey;
  status: OpenerDescalceAccountStatus | undefined;
}) => {
  const source = status?.source ?? 'starting';
  const lastSuccess =
    status?.lastSuccessAt != null
      ? new Date(status.lastSuccessAt).toLocaleTimeString()
      : 'never';
  const title =
    `${ACCOUNT_LABEL[account]} account · source: ${source} · last success: ${lastSuccess}` +
    (status?.redeemableExcluded
      ? ` · ${status.redeemableExcluded} redeemable row(s) excluded from counts`
      : '') +
    (status?.lastError ? ` · ${status.lastError}` : '');
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        label={`${ACCOUNT_LABEL[account]}: ${source}`}
        color={SOURCE_COLOR[source]}
        variant="outlined"
        data-account={account}
        data-account-source={source}
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    </Tooltip>
  );
};

/**
 * Header strip: visible-row count, total descalce (contracts, NON-ambiguous
 * rows only — labelled as such), ambiguous/resolving counts, and one
 * freshness/source chip per account.
 */
export const DescalceHeaderStrip = ({
  totals,
  snapshot,
  resolvingHidden
}: {
  totals: DescalceTotals;
  snapshot: OpenerDescalceSnapshot | undefined;
  /** Rows the `hide settled` filter is suppressing. Reported so the filter
   *  never hides a residual without saying so. */
  resolvingHidden: number;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    flexWrap="wrap"
    sx={{ mb: 1 }}
  >
    <Tooltip title="Row count and total are scoped to the currently visible (filtered) rows — clear the filters to see the whole snapshot.">
      <Typography sx={{ fontSize: '0.75rem', color: 'grey.400' }}>
        {totals.rowCount} row{totals.rowCount === 1 ? '' : 's'} shown
      </Typography>
    </Tooltip>
    <Tooltip title="Sum of venue.descalce over non-ambiguous, currently-visible (filtered) rows only — an ambiguous claimant carries the full market size, so including it would double-count.">
      <Typography
        data-total-descalce={totals.totalDescalce}
        sx={{ fontSize: '0.75rem', color: 'grey.400' }}
      >
        Total descalce (shown, excl. ambiguous):{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'common.white' }}>
          {Number.isInteger(totals.totalDescalce)
            ? totals.totalDescalce
            : totals.totalDescalce.toFixed(2)}
        </Box>
      </Typography>
    </Tooltip>
    {totals.ambiguousCount > 0 && (
      <Chip
        size="small"
        label={`${totals.ambiguousCount} ambiguous`}
        variant="outlined"
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    )}
    {totals.resolvingCount > 0 && (
      <Chip
        size="small"
        label={`${totals.resolvingCount} resolving`}
        variant="outlined"
        color="warning"
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    )}
    {resolvingHidden > 0 && (
      <Tooltip title="Rows hidden by the “hide settled” filter — one leg already resolved at its venue, but the row still carries a residual imbalance. Counts EVERY hidden row, ambiguous ones included, so it will not match the “resolving” count above, which excludes them. Turn the filter off to review them.">
        <Chip
          size="small"
          label={`${resolvingHidden} settled hidden`}
          variant="outlined"
          data-resolving-hidden={resolvingHidden}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    <Box sx={{ flex: 1 }} />
    <AccountChip account="prod" status={snapshot?.accounts?.prod} />
    <AccountChip account="test" status={snapshot?.accounts?.test} />
  </Stack>
);
